import React, { useState, useEffect, forwardRef } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';
import Input from './Input';
import { cn } from '../../lib/utils';

const NumberInput = forwardRef(({ value, onChange, step = 0.01, min, max, placeholder = 'Enter amount', className, hideButtons = true, ...props }, ref) => {
 const [displayValue, setDisplayValue] = useState('');

 useEffect(() => {
 if (value !== undefined && value !== null && value !== '') {
 setDisplayValue(value.toString());
 } else {
 setDisplayValue('');
 }
 }, [value]);

 const formatNumber = (num) => {
 if (!num && num !== 0) return '';
 const numStr = num.toString().replace(/,/g, '');
 const numValue = parseFloat(numStr);
 if (isNaN(numValue)) return '';
 return numValue.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
 };

 const handleChange = (e) => {
 const input = e.target.value.replace(/[^0-9.-]/g, '');
 setDisplayValue(input);
 const numValue = parseFloat(input);
 if (!isNaN(numValue)) {
 let newValue = numValue;
 if (min !== undefined && newValue < min) newValue = min;
 if (max !== undefined && newValue > max) newValue = max;
 onChange(newValue);
 } else if (input === '' || input === '-') {
 onChange('');
 }
 };

 const handleBlur = () => {
 if (displayValue !== '') {
 const numValue = parseFloat(displayValue.replace(/,/g, ''));
 if (!isNaN(numValue)) {
 setDisplayValue(formatNumber(numValue));
 }
 }
 };

 const handleIncrement = () => {
 const current = parseFloat(displayValue.replace(/,/g, '')) || 0;
 const newValue = current + step;
 const finalValue = max !== undefined ? Math.min(newValue, max) : newValue;
 setDisplayValue(finalValue.toString());
 onChange(finalValue);
 };

 const handleDecrement = () => {
 const current = parseFloat(displayValue.replace(/,/g, '')) || 0;
 const newValue = current - step;
 const finalValue = min !== undefined ? Math.max(newValue, min) : newValue;
 setDisplayValue(finalValue.toString());
 onChange(finalValue);
 };

 const hasError = typeof value === 'number' && value < 0;

 return (
 <div className="relative w-full">
 <div className="flex items-center w-full">
 <Input
 ref={ref}
 type="text"
 value={displayValue}
 onChange={handleChange}
 onBlur={handleBlur}
 placeholder={placeholder}
 className={cn(
 'flex-1 min-w-0 w-full',
 hideButtons ? 'pr-3' : 'pr-2',
 hasError && 'text-destructive',
 className
 )}
 {...props}
 />
 {!hideButtons && (
 <div className="flex flex-col border-l border-input">
 <button
 type="button"
 onClick={handleIncrement}
 className="flex items-center justify-center h-5 w-8 border-b border-input hover:bg-accent transition-colors"
 tabIndex={-1}
 >
 <ChevronUp className="h-3 w-3" />
 </button>
 <button
 type="button"
 onClick={handleDecrement}
 className="flex items-center justify-center h-5 w-8 hover:bg-accent transition-colors"
 tabIndex={-1}
 >
 <ChevronDown className="h-3 w-3" />
 </button>
 </div>
 )}
 </div>
 {hasError && (
 <p className="text-xs text-destructive mt-1">Cannot be negative</p>
 )}
 </div>
 );
});

NumberInput.displayName = 'NumberInput';

export default NumberInput;

